// Živá spojení se serverem (SSE GET /v1/stream): návrhy Buddyho, zpracované poznámky, nové poptávky a spárování Telegramu
// se objeví bez obnovení stránky. EventSource posílá cookie session (`withCredentials`), při výpadku se
// spojení znovu naváže s rostoucím odstupem (1 s, 2 s, 4 s ... nejvýš 30 s).
import { API_PREFIX, type StreamEvent } from '@wac-buddy/shared'
import { BASE } from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import { useBuddyStore } from '@/stores/buddy'
import { useChannelsStore } from '@/stores/channels'
import { useJobsStore } from '@/stores/jobs'
import { useTodayStore } from '@/stores/today'

export const STREAM_EVENT_TYPES = [
  'hello',
  'proposal.created',
  'proposal.decided',
  'note.processed',
  'lead.created',
  'channel.linked',
  'superseded',
] as const

export const RECONNECT_MIN_MS = 1000
export const RECONNECT_MAX_MS = 30_000

/** Co z EventSource potřebujeme (a co umí falešný zdroj v testech). */
export interface StreamSource {
  onopen: ((ev: Event) => unknown) | null
  onerror: ((ev: Event) => unknown) | null
  addEventListener(type: string, listener: (ev: MessageEvent) => void): void
  close(): void
}

export interface StreamClientOptions {
  onEvent: (event: StreamEvent) => void
  url?: string
  createSource?: (url: string) => StreamSource
  setTimer?: (fn: () => void, ms: number) => unknown
  clearTimer?: (id: unknown) => void
}

export interface StreamClient {
  start(): void
  stop(): void
}

export const STREAM_URL = `${BASE}${API_PREFIX}/stream`

function defaultSource(url: string): StreamSource {
  return new EventSource(url, { withCredentials: true }) as unknown as StreamSource
}

/** Událost z pole `data`; název události doplní `type`, když v datech chybí. */
function parseEvent(type: string, raw: unknown): StreamEvent | null {
  if (typeof raw !== 'string') return null
  try {
    const data = JSON.parse(raw) as Record<string, unknown> | null
    if (typeof data !== 'object' || data === null) return null
    return { type, ...data } as StreamEvent
  } catch {
    return null
  }
}

export function createStreamClient(options: StreamClientOptions): StreamClient {
  const url = options.url ?? STREAM_URL
  const createSource = options.createSource ?? defaultSource
  const setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms))
  const clearTimer =
    options.clearTimer ?? ((id) => clearTimeout(id as ReturnType<typeof setTimeout>))

  let running = false
  let superseded = false
  let source: StreamSource | null = null
  let timer: unknown = null
  let delay = RECONNECT_MIN_MS

  function closeSource(): void {
    if (!source) return
    source.onopen = null
    source.onerror = null
    source.close()
    source = null
  }

  function scheduleReconnect(): void {
    if (!running || superseded || timer !== null) return
    timer = setTimer(() => {
      timer = null
      connect()
    }, delay)
    delay = Math.min(delay * 2, RECONNECT_MAX_MS)
  }

  function connect(): void {
    if (!running || superseded) return
    closeSource()
    let current: StreamSource
    try {
      current = createSource(url)
    } catch {
      scheduleReconnect()
      return
    }
    source = current
    current.onopen = () => {
      delay = RECONNECT_MIN_MS
    }
    current.onerror = () => {
      // Server spojení zavřel nebo vypadlo: zavřeme ho sami a navážeme nové po odstupu.
      closeSource()
      scheduleReconnect()
    }
    for (const type of STREAM_EVENT_TYPES) {
      current.addEventListener(type, (ev) => {
        const event = parseEvent(type, ev.data)
        if (!event) return
        options.onEvent(event)
        if (event.type === 'superseded') {
          superseded = true
          if (timer !== null) clearTimer(timer)
          timer = null
          closeSource()
        }
      })
    }
  }

  return {
    start() {
      if (running) return
      running = true
      superseded = false
      delay = RECONNECT_MIN_MS
      connect()
    },
    stop() {
      running = false
      if (timer !== null) clearTimer(timer)
      timer = null
      closeSource()
    },
  }
}

/** Promítne událost ze serveru do stavu aplikace. */
export function handleStreamEvent(event: StreamEvent): void {
  const buddy = useBuddyStore()
  const today = useTodayStore()
  switch (event.type) {
    case 'hello':
      // Po (opětovném) připojení mohly nějaké události uniknout: načtené seznamy se tiše obnoví.
      if (buddy.status === 'ready') void buddy.load(true)
      void today.refresh()
      break
    case 'proposal.created':
      buddy.applyProposal(event.proposal)
      void today.refresh()
      break
    case 'proposal.decided':
      buddy.applyDecision(event.proposal)
      break
    case 'note.processed':
      // Návrhy z poznámky nebo hlasovky jsou na světě: čekající návrhy se obnoví.
      if (buddy.status === 'ready') void buddy.load(true)
      void today.refresh()
      break
    case 'lead.created': {
      const jobs = useJobsStore()
      if (jobs.status === 'ready') void jobs.load()
      void today.refresh()
      break
    }
    case 'channel.linked':
      // Sběrnice je per firma: spárování jiného člena se nás netýká.
      if (useAuthStore().me?.user.id === event.userId) void useChannelsStore().load(true)
      break
    default:
      break
  }
}

/** Spojení pro aplikaci: bez podpory EventSource (staré prohlížeče, testy) nedělá nic. */
export function createAppStream(): StreamClient {
  if (typeof EventSource === 'undefined') return { start() {}, stop() {} }
  return createStreamClient({ onEvent: handleStreamEvent })
}
